import { getPermissions, hasPermission, type Permission, type UserRole } from '@valiosascrm/shared';

export const ROLE_CATALOG_VERSION = 1;

export interface RoleTourStep {
  route: string;
  anchor: string;
  title: string;
  body: string;
  permission?: Permission;
}

export interface RoleTour {
  id: string;
  goal: string;
  steps: RoleTourStep[];
}

export interface RoleCatalogEntry {
  label: string;
  audience: string;
  accessModel: string;
  permissions: readonly Permission[];
  primaryRoutes: string[];
  restrictedRoutes?: string[];
  restrictedActions?: Permission[];
  dataScope?: Record<string, string>;
  tour: RoleTour;
}

const adminSteps: RoleTourStep[] = [
  {
    route: '/primeiros-passos',
    anchor: 'onboarding-progress',
    title: 'Primeiros Passos',
    body: 'Acompanhe a configuração da clínica, fases e conquistas.',
    permission: 'dashboard:view',
  },
  {
    route: '/config/clinica',
    anchor: 'clinic-profile',
    title: 'Dados da clínica',
    body: 'Revise identidade, horários e informações usadas no app.',
    permission: 'settings:manage',
  },
  {
    route: '/config/servicos',
    anchor: 'services-catalog',
    title: 'Serviços',
    body: 'Cadastre procedimentos, preços, duração e status ativo.',
    permission: 'settings:view',
  },
  {
    route: '/config/equipe',
    anchor: 'team-roles',
    title: 'Equipe e profissionais',
    body: 'Gerencie a equipe, quem atende, serviços vinculados e disponibilidade — tudo em um lugar.',
    permission: 'settings:view',
  },
  {
    route: '/config/equipe',
    anchor: 'team-roles',
    title: 'Equipe e permissões',
    body: 'Convide usuários e defina papéis por responsabilidade.',
    permission: 'settings:view',
  },
  {
    route: '/config/whatsapp',
    anchor: 'whatsapp-connection',
    title: 'WhatsApp',
    body: 'Conecte o número da clínica e libere o atendimento.',
    permission: 'whatsapp:manage',
  },
  {
    route: '/inbox',
    anchor: 'inbox-assignment',
    title: 'Inbox',
    body: 'Atenda, atribua responsáveis e acompanhe conversas.',
    permission: 'inbox:read',
  },
  {
    route: '/analytics',
    anchor: 'analytics-overview',
    title: 'Analytics',
    body: 'Monitore desempenho, receita e risco operacional.',
    permission: 'analytics:view',
  },
];

const secretarySteps: RoleTourStep[] = [
  {
    route: '/dashboard',
    anchor: 'daily-summary',
    title: 'Resumo do dia',
    body: 'Veja agenda, alertas e indicadores principais.',
    permission: 'dashboard:view',
  },
  {
    route: '/inbox',
    anchor: 'conversation-list',
    title: 'Atendimento',
    body: 'Responda conversas, use respostas rápidas e atribua responsáveis.',
    permission: 'inbox:read',
  },
  {
    route: '/crm',
    anchor: 'crm-kanban',
    title: 'CRM',
    body: 'Organize leads por etapa e acompanhe oportunidades.',
    permission: 'leads:read',
  },
  {
    route: '/agendamentos',
    anchor: 'calendar-board',
    title: 'Agenda',
    body: 'Crie, remarque e cancele atendimentos.',
    permission: 'appointments:read',
  },
  {
    route: '/pacientes',
    anchor: 'patient-list',
    title: 'Pacientes',
    body: 'Cadastre, edite, filtre e exporte pacientes.',
    permission: 'patients:read',
  },
  {
    route: '/campanhas',
    anchor: 'campaign-wizard',
    title: 'Campanhas',
    body: 'Crie disparos únicos para segmentos de pacientes e leads.',
    permission: 'campaigns:read',
  },
  {
    route: '/financeiro',
    anchor: 'financial-overview',
    title: 'Financeiro',
    body: 'Registre movimentações e acompanhe caixa.',
    permission: 'financial:read',
  },
];

const professionalSteps: RoleTourStep[] = [
  {
    route: '/dashboard',
    anchor: 'professional-summary',
    title: 'Seu dia',
    body: 'Veja seus próximos atendimentos e indicadores essenciais.',
    permission: 'dashboard:view',
  },
  {
    route: '/agendamentos',
    anchor: 'calendar-own-appointments',
    title: 'Sua agenda',
    body: 'Acompanhe e atualize atendimentos atribuídos a você.',
    permission: 'appointments:read',
  },
  {
    route: '/pacientes',
    anchor: 'own-patient-list',
    title: 'Seus pacientes',
    body: 'Acesse pacientes relacionados aos seus atendimentos.',
    permission: 'patients:read',
  },
  {
    route: '/pacientes/:id',
    anchor: 'patient-clinical-timeline',
    title: 'Histórico clínico',
    body: 'Consulte dados, evolução e registros do paciente.',
    permission: 'patients:read',
  },
  {
    route: '/pacientes/:id/anamnese',
    anchor: 'anamnesis-form',
    title: 'Anamnese',
    body: 'Registre informações clínicas e fotos quando aplicável.',
    permission: 'patients:read',
  },
  {
    route: '/inbox',
    anchor: 'assigned-conversations',
    title: 'Conversas atribuídas',
    body: 'Veja conversas direcionadas para você.',
    permission: 'inbox:read',
  },
];

export const ROLE_CATALOG: Record<UserRole, RoleCatalogEntry> = {
  admin: {
    label: 'Administrador',
    audience: 'Dono ou gestor da clínica',
    accessModel: 'Acesso completo ao tenant e governança da operação',
    permissions: getPermissions('admin'),
    primaryRoutes: [
      '/primeiros-passos',
      '/dashboard',
      '/inbox',
      '/crm',
      '/agendamentos',
      '/pacientes',
      '/financeiro',
      '/analytics',
      '/relatorios',
      '/ai/churn',
      '/ai/forecast',
      '/unidades',
      '/estoque',
      '/campanhas',
      '/config',
      '/config/whatsapp',
      '/config/webhooks',
      '/billing',
      '/pricing',
    ],
    tour: {
      id: 'admin-foundation-v1',
      goal: 'Configurar, governar e medir a clínica inteira',
      steps: adminSteps,
    },
  },
  secretary: {
    label: 'Secretaria',
    audience: 'Recepção e operação diária',
    accessModel: 'Atendimento, agenda, CRM, pacientes e operação financeira sem configurações críticas',
    permissions: getPermissions('secretary'),
    primaryRoutes: [
      '/dashboard',
      '/inbox',
      '/crm',
      '/agendamentos',
      '/pacientes',
      '/financeiro',
      '/analytics',
      '/relatorios',
      '/estoque',
      '/campanhas',
      '/billing',
      '/pricing',
    ],
    restrictedRoutes: ['/config', '/config/whatsapp', '/config/webhooks', '/unidades', '/ai/churn', '/ai/forecast'],
    tour: {
      id: 'secretary-operations-v1',
      goal: 'Operar o atendimento do dia em poucos cliques',
      steps: secretarySteps,
    },
  },
  professional: {
    label: 'Profissional',
    audience: 'Profissional clínico ou executor de atendimento',
    accessModel: 'Dados próprios, agenda, pacientes atendidos e conversas atribuídas',
    permissions: getPermissions('professional'),
    dataScope: {
      appointments: 'Próprios atendimentos quando filtro profissional estiver aplicado',
      patients: 'Pacientes vinculados aos próprios atendimentos',
      inbox: 'Apenas conversas atribuídas ao usuário, sem inbox:view-all',
    },
    primaryRoutes: ['/dashboard', '/inbox', '/crm', '/agendamentos', '/pacientes', '/pacientes/:id', '/pacientes/:id/anamnese'],
    restrictedActions: [
      'patients:create',
      'patients:delete',
      'patients:export',
      'appointments:create',
      'appointments:cancel',
      'leads:create',
      'leads:update',
      'leads:archive',
      'leads:convert',
      'inbox:write',
      'inbox:assign',
      'inbox:manage',
      'settings:view',
      'financial:read',
      'analytics:view',
      'reports:view',
    ],
    tour: {
      id: 'professional-clinical-v1',
      goal: 'Executar atendimentos com contexto clínico e foco nos próprios pacientes',
      steps: professionalSteps,
    },
  },
  // Story 33.9: owner = admin + governança cross-clínica (Membership admin + isOwner).
  // Reusa o tour do admin (superset); as rotas /owner/* (cross-clínica) chegam em
  // stories posteriores de owner-routes.
  owner: {
    label: 'Proprietário',
    audience: 'Dono de uma ou mais clínicas',
    accessModel: 'Acesso completo ao tenant ativo + governança cross-clínica (clínicas, membros, faturamento e analytics consolidados)',
    permissions: getPermissions('owner'),
    primaryRoutes: [
      '/primeiros-passos',
      '/dashboard',
      '/inbox',
      '/crm',
      '/agendamentos',
      '/pacientes',
      '/financeiro',
      '/analytics',
      '/relatorios',
      '/ai/churn',
      '/ai/forecast',
      '/unidades',
      '/estoque',
      '/campanhas',
      '/config',
      '/config/whatsapp',
      '/config/webhooks',
      '/billing',
      '/pricing',
    ],
    tour: {
      id: 'admin-foundation-v1',
      goal: 'Configurar, governar e medir a clínica inteira',
      steps: adminSteps,
    },
  },
};

export function getRoleCatalog(role: UserRole): RoleCatalogEntry {
  return ROLE_CATALOG[role];
}

export function getTourForRole(role: UserRole): RoleTour {
  return getRoleCatalog(role).tour;
}

export function getTourProgressKey(role: UserRole, tourId: string): string {
  return `${role}:${tourId}:v${ROLE_CATALOG_VERSION}`;
}

export function isStaticTourRoute(route: string): boolean {
  return !route.split('/').some((part) => part.startsWith(':'));
}

export function routeTemplateMatches(template: string, pathname: string): boolean {
  const normalize = (value: string) => value.replace(/\/+$/, '') || '/';
  const templateParts = normalize(template).split('/').filter(Boolean);
  const pathParts = normalize(pathname).split('/').filter(Boolean);
  if (templateParts.length !== pathParts.length) return false;
  return templateParts.every((part, index) => part.startsWith(':') || part === pathParts[index]);
}

export function canRoleUseTourStep(role: UserRole, step: RoleTourStep): boolean {
  return !step.permission || hasPermission(role, step.permission);
}

export function findNextTourStepIndex(args: {
  role: UserRole;
  tour: RoleTour;
  fromIndex: number;
  pathname: string;
  anchorExists: (anchor: string) => boolean;
  warn?: (message: string) => void;
}): number | null {
  const { role, tour, fromIndex, pathname, anchorExists, warn } = args;

  for (let index = fromIndex; index < tour.steps.length; index += 1) {
    const step = tour.steps[index];
    if (!canRoleUseTourStep(role, step)) {
      warn?.(`Tour step skipped without permission: ${step.route}#${step.anchor}`);
      continue;
    }

    const routeMatches = routeTemplateMatches(step.route, pathname);
    if (!isStaticTourRoute(step.route) && !routeMatches) {
      warn?.(`Tour step skipped because dynamic route is not active: ${step.route}`);
      continue;
    }

    if (routeMatches && !anchorExists(step.anchor)) {
      warn?.(`Tour step skipped because anchor is missing: ${step.anchor}`);
      continue;
    }

    return index;
  }

  return null;
}
